// This is a partial refactor of AllSocialMediaPosts to use Ant Design
// while maintaining most of the original logic. Assumes AntD is installed.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tabs, Row, Col, Typography, Flex, theme } from '@app/ui';
import moment from 'moment';
import * as R from 'ramda';
import connect from '@app/utils/connect';
import getAllPosts from './api/getAllPosts';
import sendNotification from '@app/actions/notifications/send';
import useCampaignsInsights from '@app/components/hooks/campaigns/useCampaignsInsights';
import useCampaigns from '@app/components/hooks/campaigns/useCampaigns';
import AuthenticatedPostsFilters from './components/AuthenticatedPostsFilters';
import AuthenticatedPostsTable from './components/AuthenticatedPostsTable';
import CampaignsInsightsTable from './components/CampaignsInsightsTable';
import {
    downloadAuthenticatedSocialPostsAsCsv,
    downloadCampaignsSummaryAsCsv,
} from './utils';
import combineActions from '@app/utils/combineActions';
import { DATE_MOMENT_FORMAT } from '@app/constants/dateFormat';
import {
    ALL_POSTS_TABS,
    POSTS_LIMIT_PER_REQUEST,
    POSTS_SORTING_PARAMETER_KEYS,
} from './constants';
import { Campaign, Client } from '@app/types/api';

const AllSocialMediaPosts = ({
    client,
    user,
    sendNotification,
}: {
    client: Client;
    user: any;
    sendNotification: any;
}) => {
    const { t } = useTranslation('ownedSocial');
    const { csvDelimiter } = user;
    const [hoveredTab, setHoveredTab] = React.useState(null);
    const { data: campaigns, refetch, isSuccess } = useCampaigns(client.id);
    const [sortedByColumn, setSortedByColumn] = useState({
        name: 'createdAt',
        desc: true,
    });
    const [selectedTab, setSelectedTab] = useState(ALL_POSTS_TABS.ALL_POSTS);

    const postsCampaigns = useMemo(() => {
        const campaignsList: Campaign[] = isSuccess ? campaigns ?? [] : [];
        return campaignsList.reduce<
            Record<number, { label: string; value: number }>
        >((acc, c) => {
            acc[c.id] = { label: c.name, value: c.id };
            return acc;
        }, {});
    }, [campaigns, isSuccess]);

    const {
        posts,
        postCount,
        totals,
        isLoading: isPostsDataLoading,
        isAddingPage,
        queryParams,
        loadMorePosts,
        setAllPostsQueryParams,
    } = getAllPosts(client.id);

    const campaignsQueryParams = useMemo(
        () =>
            R.evolve(
                {
                    domainObject: {
                        content: R.always(null),
                        orderByMap: R.always(null),
                    },
                    perPage: R.always(null),
                    startIndex: R.always(null),
                },
                queryParams,
            ),
        [queryParams],
    );

    const { isLoading: isCampaignsLoading, data: campaignsInsights } =
        useCampaignsInsights(campaignsQueryParams, client.id);

    const shouldDisplayLoadMoreButton =
        !isPostsDataLoading &&
        !isAddingPage &&
        posts?.length > 0 &&
        posts.length % POSTS_LIMIT_PER_REQUEST === 0 &&
        selectedTab === ALL_POSTS_TABS.ALL_POSTS;

    const handleOnFilterPosts = filters => {
        const { mediaTypeIds, carouselMediaTypeIds } =
            filters['Media Type'].filterValue || {};
        const domainObject = {
            ...queryParams.domainObject,
            content: filters.Caption.value,
            accountIds: Object.keys(filters.Accounts.value).map(
                id => id.split('-')[1] || id,
            ),
            mediaTypeIds,
            carouselMediaTypeIds,
            socialCampaignsIds: Object.keys(filters.Campaigns.value),
            startDate: filters.startDate.value
                ? moment(filters.startDate.value)
                      .startOf('day')
                      .format(DATE_MOMENT_FORMAT)
                : null,
            endDate: filters.endDate.value
                ? moment(filters.endDate.value)
                      .endOf('day')
                      .format(DATE_MOMENT_FORMAT)
                : null,
        };

        setAllPostsQueryParams({ ...queryParams, domainObject, startIndex: 0 });
    };

    const handleTableSorting = sortedColumnId => {
        const isDesc = !(
            sortedColumnId === sortedByColumn.name && sortedByColumn.desc
        );
        const queryParam = POSTS_SORTING_PARAMETER_KEYS[sortedColumnId];
        const newQueryParams = { ...queryParams };

        newQueryParams.domainObject.orderByMap = {
            [queryParam]: isDesc ? 'desc' : 'asc',
        };

        setSortedByColumn({ name: sortedColumnId, desc: isDesc });
        setAllPostsQueryParams(newQueryParams);
    };

    const _downloadPostsAsCsv = async () => {
        try {
            const csvQueryParams = {
                ...queryParams,
                domainObject: {
                    ...queryParams.domainObject,
                    groupCarouselPosts: true,
                },
            };
            await downloadAuthenticatedSocialPostsAsCsv(
                csvQueryParams,
                client.id,
                csvDelimiter,
                sendNotification,
                t,
            );
        } catch (e) {
            sendNotification(
                e.status === 504 || e.status === 408
                    ? t('socialPostMetrics.errors.csvDownloadDelay')
                    : e.message,
                {
                    context: 'warning',
                    description: t('socialPostMetrics.errors.csvDownloadRetry'),
                },
            );
        }
    };

    const _downloadCampaignsInsightsAsCsv = () =>
        downloadCampaignsSummaryAsCsv(
            campaignsInsights,
            csvDelimiter,
            sendNotification,
            t,
        );

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const items = React.useMemo(() => {
        const tabLabels = {
            [ALL_POSTS_TABS.ALL_POSTS]: t('socialPostMetrics.tabs.allPosts'),
            [ALL_POSTS_TABS.CAMPAIGNS]: t('socialPostMetrics.tabs.campaigns'),
        };

        return Object.values(ALL_POSTS_TABS)
            .map(tab => {
                const isActive = selectedTab === tab;
                const isHovered = hoveredTab === tab;

                const iconClass =
                    tab === ALL_POSTS_TABS.ALL_POSTS ? 'memo' : 'megaphone';

                return {
                    key: tab,
                    label: (
                        <div
                            onMouseEnter={() => setHoveredTab(tab)}
                            onMouseLeave={() => setHoveredTab(null)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                            }}
                        >
                            <i
                                className={`fa-regular fa-${iconClass}`}
                                style={{
                                    color:
                                        isActive || isHovered
                                            ? colorPrimary
                                            : '#515252',
                                    width: 16,
                                    height: 16,
                                    fontSize: 16,
                                }}
                            />
                            <Typography.Text
                                strong={false}
                                style={{
                                    color:
                                        isActive || isHovered
                                            ? colorPrimary
                                            : '#515252',
                                }}
                            >
                                {tabLabels[tab]}
                            </Typography.Text>
                        </div>
                    ),
                };
            })
            .filter(Boolean);
    }, [client.permissions, selectedTab, hoveredTab, t, colorPrimary]);

    return (
        <Flex
            className="all-posts-page"
            vertical
            style={{
                padding: 24,
            }}
            gap={12}
        >
            <Row justify="start">
                <Col span={24}>
                    <Typography.Title
                        level={4}
                        style={{
                            margin: 0,
                            color: '#1f1f1f',
                            fontWeight: 400,
                            fontSize: 32,
                        }}
                    >
                        {t('socialPostMetrics.title')}
                    </Typography.Title>
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <Tabs
                        activeKey={selectedTab}
                        items={items}
                        onChange={setSelectedTab}
                    />
                </Col>
            </Row>

            <AuthenticatedPostsFilters
                currentTab={selectedTab}
                clientId={client.id}
                isDataLoading={isPostsDataLoading}
                onFilterPosts={handleOnFilterPosts}
                postsCampaigns={postsCampaigns}
            />

            <Row>
                <Col span={24}>
                    {selectedTab === ALL_POSTS_TABS.ALL_POSTS ? (
                        <AuthenticatedPostsTable
                            client={client}
                            postsData={{ posts, totals, postCount }}
                            isDataLoading={isPostsDataLoading}
                            sortTableByColumn={handleTableSorting}
                            sortedByColumn={sortedByColumn}
                            downloadCsv={_downloadPostsAsCsv}
                            queryParams={queryParams}
                            sendNotification={sendNotification}
                            updateCampaignOptionsInFilters={() => refetch()}
                        />
                    ) : (
                        <CampaignsInsightsTable
                            campaignsInsights={campaignsInsights}
                            isLoading={isCampaignsLoading}
                            onDownloadCsv={_downloadCampaignsInsightsAsCsv}
                            query={campaignsQueryParams}
                        />
                    )}
                </Col>
            </Row>

            {shouldDisplayLoadMoreButton && (
                <Row justify="center" style={{ marginTop: 24 }}>
                    <Button
                        type="primary"
                        size="middle"
                        onClick={loadMorePosts}
                        disabled={isPostsDataLoading || isAddingPage}
                    >
                        {t('socialPostMetrics.loadMore')}
                    </Button>
                </Row>
            )}
        </Flex>
    );
};

export default connect(
    ['client', 'user'],
    combineActions({ sendNotification }),
)(AllSocialMediaPosts);
